import { createContext, useContext, useEffect, useState } from "react";
import { authApi, authTenantServiceApi, refreshAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Resolves which Auth-platform tenant an email belongs to, via
 * tis-auth-tenant-service's own public GET /tenants/resolve (see
 * contracts doc section 1 and that route's doc comment in
 * tis-auth-tenant-service/src/tenant/tenant.controller.ts) — same
 * mechanism tis-spa-talent and tis-spa-hub already use for their own
 * login screens, called directly here rather than proxied through
 * Tenant/backend (which does not own tenant identity — decision 002).
 *
 * KNOWN GAP (tracked, not silently assumed correct): there is no
 * established mechanism yet, anywhere in tis-auth, for a genuine
 * platform-operator principal (tenant_id === null) to log in — every
 * documented login path resolves a real business tenant first. This
 * context implements the tenant-resolving path (verified against real
 * tis-auth-tenant-service source) and ASSUMES platform operators are a
 * role granted on a real user within some tenant context (checked via
 * the `roles` claim after login) rather than a tenant-less principal.
 * If tis-auth-agent later confirms a different mechanism, this file's
 * login() must change — this is flagged for tis-auth-agent review
 * before any real platform-operator account exists.
 */
async function resolveTenantByEmail(email) {
  // Resolve the tenant by email via POST with the email in the BODY —
  // never a GET ?email= query param. The email is PII and account-
  // enumeration-sensitive; a query string leaks it into access logs,
  // proxy/CDN logs, traces, browser history and Referer headers. The
  // body keeps it out of all of those.
  //
  // ROLLOUT: during the window where the tenant-service container may
  // still be the GET-only build, fall back to the (deprecated) GET so
  // login keeps working. Remove the fallback once the service exposes
  // POST /tenants/resolve everywhere.
  try {
    const res = await authTenantServiceApi.post("/api/v1/tenants/resolve", { email });
    return res?.data ?? null;
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    // A FRAMEWORK "route not found" 404 (old GET-only container) looks like
    // { error: "Not Found", message: "Cannot POST ..." } with NO `code`.
    // A real "no tenant for this email" 404 carries { code: "NOT_FOUND" }.
    const routeMissing = status === 404 && data && !data.code;
    if (routeMissing) {
      const res = await authTenantServiceApi.get("/api/v1/tenants/resolve", { params: { email } }).catch(() => null);
      return res?.data ?? null;
    }
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    let cancelled = false;

    // Decode a JWT payload without verifying (verification is the
    // backend's job) — we only need `exp`/`sub`/`tid`/`roles` to decide
    // what to render. Returns null for a malformed token.
    const decode = (t) => {
      try {
        return JSON.parse(atob(t.split(".")[1]));
      } catch {
        return null;
      }
    };

    const applyToken = (t) => {
      const payload = decode(t);
      if (!payload) return false;
      setUser({ id: payload.sub, tenantId: payload.tid ?? null });
      setRoles(payload.roles || []);
      return true;
    };

    const bootstrap = async () => {
      const token = localStorage.getItem("tenant_platform_token");
      const refreshToken = localStorage.getItem("tenant_platform_refresh_token");
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }

      const payload = decode(token);
      const nowSec = Math.floor(Date.now() / 1000);
      // Treat a token expiring within 10s as already expired (clock skew +
      // in-flight latency). A missing exp is treated as "trust it".
      const expired = payload?.exp ? payload.exp <= nowSec + 10 : false;

      // Valid, unexpired token → use it directly.
      if (payload && !expired) {
        applyToken(token);
        setLoading(false);
        return;
      }

      // Expired (or malformed) token but we hold a refresh token → silently
      // mint a fresh access token instead of logging the user out. This is
      // the page-reload case the user hit: never show "Access token has
      // expired," just refresh behind the scenes.
      if (refreshToken) {
        try {
          const fresh = await refreshAccessToken();
          if (cancelled) return;
          if (applyToken(fresh)) {
            setLoading(false);
            return;
          }
        } catch {
          /* fall through to logged-out */
        }
      }

      if (cancelled) return;
      localStorage.removeItem("tenant_platform_token");
      setUser(false);
      setLoading(false);
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    const resolved = await resolveTenantByEmail(email);
    const tenantId = resolved?.tenantId;
    if (!tenantId) {
      // Deliberately the SAME generic error tis-auth-service itself
      // returns for a wrong password (see tis-talent-auth-service's
      // login() doc comment: "never distinguishing 'we don't know this
      // email' from 'wrong password,' for the same account-enumeration
      // reasons the platform's own auth-service already documents").
      // An earlier version of this file said "No account found for this
      // email," which leaks account existence to an attacker probing
      // emails — found during task 10 security review.
      throw { response: { data: { detail: "Invalid email or password" } } };
    }
    const res = await authApi.post("/auth/login", { email, password }, { headers: { "x-tis-tenant-id": tenantId } });
    localStorage.setItem("tenant_platform_token", res.data.access_token);
    localStorage.setItem("tenant_platform_refresh_token", res.data.refresh_token);
    localStorage.setItem("tenant_platform_tenant_id", tenantId);

    const payload = JSON.parse(atob(res.data.access_token.split(".")[1]));
    setUser({ id: payload.sub, tenantId: payload.tid ?? null });
    setRoles(payload.roles || []);
    return payload;
  };

  const logout = () => {
    localStorage.removeItem("tenant_platform_token");
    localStorage.removeItem("tenant_platform_refresh_token");
    localStorage.removeItem("tenant_platform_tenant_id");
    setUser(false);
    setRoles([]);
    window.location.href = "/login";
  };

  const hasRole = (code) => roles.some((r) => r.code === code);
  const isPlatformOperator = () => hasRole("P-SA") || hasRole("P-OPS");
  // Admin tier allowed to edit/delete tenant settings: the Company Owner
  // (OWN, super admin) and Tenant System Admin (TSA), plus platform
  // operators. Recruiters/managers/employees are NOT admins and get a
  // read-only view of these pages. Mirrors the OWN/TSA grants the
  // tis-auth-rbac-service seeds and the tenant-service enforces on PATCH.
  const isAdmin = () => hasRole("OWN") || hasRole("TSA") || isPlatformOperator();

  return (
    <AuthContext.Provider value={{ user, loading, roles, login, logout, hasRole, isPlatformOperator, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
